<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';

import { useDocumentTypesStore } from 'dashboard/stores/documentTypes';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import DocumentTypeDialog from 'dashboard/components-next/CRM/Documents/DocumentTypeDialog/DocumentTypeDialog.vue';

const CHIP_CLASS = 'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium';

const store = useDocumentTypesStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const addDialogRef = ref(null);
const editDialogRef = ref(null);
const deleteDialogRef = ref(null);
const selectedType = ref(null);
const pendingDelete = ref(null);

const documentTypes = computed(() => store.getDocumentTypes);
const uiFlags = computed(() => store.getUIFlags);
const isFetching = computed(() => uiFlags.value.fetching && !store.hasLoaded);

const fetchTypes = async () => {
  try {
    await store.get({ force: true });
  } catch {
    // The error state below offers a retry.
  }
};

onMounted(fetchTypes);

const goToDocuments = () => {
  router.push({
    name: 'crm_documents_index',
    params: { accountId: route.params.accountId },
  });
};

const openAddDialog = () => addDialogRef.value?.open();

const openEditDialog = type => {
  selectedType.value = type;
  editDialogRef.value?.open();
};

const openDeleteDialog = type => {
  pendingDelete.value = type;
  deleteDialogRef.value?.open();
};

const handleAdd = async payload => {
  try {
    await store.create(payload);
    addDialogRef.value?.close();
    useAlert(t('DOCUMENTS.TYPES_SETTINGS.CREATE_SUCCESS'));
  } catch (error) {
    useAlert(t('DOCUMENTS.TYPES_SETTINGS.CREATE_ERROR'));
  }
};

const handleEdit = async payload => {
  if (!selectedType.value) return;
  try {
    await store.update(selectedType.value.id, payload);
    editDialogRef.value?.close();
    useAlert(t('DOCUMENTS.TYPES_SETTINGS.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(t('DOCUMENTS.TYPES_SETTINGS.UPDATE_ERROR'));
  }
};

const confirmDelete = async () => {
  if (!pendingDelete.value) return;
  deleteDialogRef.value?.close();
  try {
    // Referenced types are soft-deactivated; the store refetches either way.
    await store.delete(pendingDelete.value.id);
    useAlert(t('DOCUMENTS.TYPES_SETTINGS.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('DOCUMENTS.TYPES_SETTINGS.DELETE_ERROR'));
  } finally {
    pendingDelete.value = null;
  }
};
</script>

<template>
  <div class="flex flex-col flex-1 h-full m-0 overflow-auto bg-n-background">
    <header class="sticky top-0 z-10 bg-n-background">
      <div class="flex items-center justify-between w-full py-6 px-6 gap-2 mx-auto max-w-[60rem]">
        <div class="flex items-center gap-2 min-w-0">
          <Button
            v-tooltip.top="t('DOCUMENTS.DETAIL.BACK')"
            icon="i-lucide-arrow-left"
            size="sm"
            ghost
            slate
            @click="goToDocuments"
          />
          <span class="text-xl font-medium truncate text-n-slate-12">
            {{ t('DOCUMENTS.TYPES_SETTINGS.TITLE') }}
          </span>
        </div>
        <Button
          v-if="isAdmin"
          :label="t('DOCUMENTS.TYPES_SETTINGS.ADD_BUTTON')"
          icon="i-lucide-plus"
          size="sm"
          @click="openAddDialog"
        />
      </div>
    </header>

    <main class="flex-1">
      <div class="w-full mx-auto max-w-[60rem] px-6">
        <div v-if="isFetching" class="flex items-center justify-center py-10 text-n-slate-11">
          <Spinner />
        </div>

        <div
          v-else-if="uiFlags.fetchError"
          class="flex flex-col items-center justify-center gap-3 py-10"
        >
          <span class="text-base text-n-slate-11">
            {{ t('DOCUMENTS.TYPES_SETTINGS.ERROR.TITLE') }}
          </span>
          <Button
            :label="t('DOCUMENTS.TYPES_SETTINGS.ERROR.RETRY')"
            size="sm"
            slate
            @click="fetchTypes"
          />
        </div>

        <div v-else-if="!documentTypes.length" class="flex items-center justify-center py-10">
          <span class="text-base text-n-slate-11">
            {{ t('DOCUMENTS.TYPES_SETTINGS.EMPTY') }}
          </span>
        </div>

        <div v-else class="flex flex-col gap-4 pt-2 pb-6">
          <CardLayout
            v-for="type in documentTypes"
            :key="type.id"
            layout="row"
            :class="{ 'opacity-60': !type.active }"
          >
            <div class="flex flex-col gap-1 flex-1 min-w-0">
              <div class="flex flex-wrap items-center gap-2 min-w-0">
                <span class="text-base font-medium truncate text-n-slate-12">
                  {{ type.label }}
                </span>
                <span
                  v-if="!type.active"
                  :class="CHIP_CLASS"
                  class="bg-n-slate-9/10 text-n-slate-11"
                >
                  {{ t('DOCUMENTS.TYPES_SETTINGS.INACTIVE') }}
                </span>
                <span
                  v-if="type.lifecycle"
                  :class="CHIP_CLASS"
                  class="bg-n-iris-9/10 text-n-iris-11"
                >
                  {{ t('DOCUMENTS.TYPES_SETTINGS.CUSTOM_LIFECYCLE') }}
                </span>
              </div>
              <span class="text-xs font-mono text-n-slate-10">{{ type.key }}</span>
              <span v-if="type.description" class="text-sm text-n-slate-11">
                {{ type.description }}
              </span>
            </div>
            <div v-if="isAdmin" class="flex gap-1 flex-shrink-0">
              <Button
                v-tooltip.top="t('DOCUMENTS.TYPES_SETTINGS.ACTIONS.EDIT')"
                icon="i-lucide-pen"
                xs
                slate
                faded
                @click="openEditDialog(type)"
              />
              <Button
                v-tooltip.top="t('DOCUMENTS.TYPES_SETTINGS.ACTIONS.DELETE')"
                icon="i-lucide-trash-2"
                xs
                ruby
                faded
                @click="openDeleteDialog(type)"
              />
            </div>
          </CardLayout>
        </div>
      </div>
    </main>

    <DocumentTypeDialog
      ref="addDialogRef"
      mode="add"
      :is-submitting="uiFlags.creating"
      @save="handleAdd"
    />
    <DocumentTypeDialog
      ref="editDialogRef"
      mode="edit"
      :document-type="selectedType"
      :is-submitting="uiFlags.updating"
      @save="handleEdit"
    />
    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('DOCUMENTS.TYPES_SETTINGS.DELETE_DIALOG.TITLE')"
      :description="t('DOCUMENTS.TYPES_SETTINGS.DELETE_DIALOG.DESCRIPTION')"
      :confirm-button-label="t('DOCUMENTS.TYPES_SETTINGS.DELETE_DIALOG.CONFIRM')"
      @confirm="confirmDelete"
    />
  </div>
</template>
